import type {MediaAsset} from '../types';
import {apiRequest} from '../api/client';
import {fetchBlob,downloadBlob} from './media-transfer';

type StoredAsset={key:string;userId:string;media:MediaAsset;blob:Blob;serverDeletionPending:boolean};
type AssetIndex=Omit<StoredAsset,'blob'>;
const indexKey='vgen.local-asset-index.v1';
function readIndex():AssetIndex[]|null{
  const raw=localStorage.getItem(indexKey);if(raw===null)return null;
  const rows:unknown=JSON.parse(raw);
  if(!Array.isArray(rows))throw new Error('Не удалось прочитать список локальных материалов. Файлы не удалены.');
  return rows as AssetIndex[];
}
const metadata=(row:StoredAsset):AssetIndex=>({key:row.key,userId:row.userId,media:row.media,serverDeletionPending:row.serverDeletionPending});
function writeIndex(rows:AssetIndex[]){localStorage.setItem(indexKey,JSON.stringify(rows))}
async function assetIndex():Promise<AssetIndex[]>{
  const saved=readIndex();if(saved)return saved;
  // Import the original single-store format once; polling only reads small metadata afterwards.
  const rows=(await transaction<StoredAsset[]>('readonly',store=>store.getAll())).map(metadata);
  // Do not overwrite downloads that completed during the import.
  const current=readIndex();if(current)return current;
  writeIndex(rows);return rows;
}
async function indexAsset(row:StoredAsset){
  await assetIndex();const rows=readIndex()??[];
  writeIndex([metadata(row),...rows.filter(item=>item.key!==row.key)]);
}
const keyFor=(userId:string,id:string)=>`${userId}:${id}`;
async function transaction<T>(mode:IDBTransactionMode,action:(store:IDBObjectStore)=>IDBRequest<T>):Promise<T>{
  const db=await new Promise<IDBDatabase>((resolve,reject)=>{
    const request=indexedDB.open('vgen-local-assets-v1',1);
    request.onupgradeneeded=()=>request.result.createObjectStore('assets',{keyPath:'key'});
    request.onsuccess=()=>resolve(request.result);
    request.onerror=()=>reject(request.error??new Error('Локальное хранилище недоступно'));
    request.onblocked=()=>reject(new Error('Закройте другие вкладки VGen и повторите скачивание'));
  });
  try{return await new Promise<T>((resolve,reject)=>{
    const tx=db.transaction('assets',mode);let value:T;
    const request=action(tx.objectStore('assets'));
    request.onsuccess=()=>{value=request.result};
    request.onerror=()=>reject(request.error??new Error('Не удалось сохранить файл на устройстве'));
    tx.oncomplete=()=>resolve(value);
    tx.onabort=()=>reject(tx.error??new Error('Локальное сохранение прервано. Серверная копия сохранена.'));
    tx.onerror=()=>reject(tx.error??new Error('Недостаточно места на устройстве'));
  })}finally{db.close()}
}
export async function getLocalAsset(userId:string,id:string):Promise<StoredAsset|null>{
  return (await transaction<StoredAsset|undefined>('readonly',store=>store.get(keyFor(userId,id))))??null;
}
export async function listLocalAssets(userId:string):Promise<Array<Omit<StoredAsset,'blob'>>>{
  return (await assetIndex()).filter(row=>row.userId===userId);
}
export async function removeLocalAsset(userId:string,id:string):Promise<void>{
  await assetIndex();
  await transaction('readwrite',store=>store.delete(keyFor(userId,id)));
  writeIndex((readIndex()??[]).filter(row=>row.key!==keyFor(userId,id)));
}
export async function markLocalAssetServerDeleted(userId:string,id:string):Promise<void>{
  const row=await getLocalAsset(userId,id);if(row){const updated={...row,serverDeletionPending:false};await transaction('readwrite',store=>store.put(updated));await indexAsset(updated)}
}
type DownloadDependencies={fetchBlob:(url:string)=>Promise<Blob>;downloadBlob:(blob:Blob,name:string)=>void;deleteServer:(id:string)=>Promise<unknown>};
export async function downloadMediaAsset(media:MediaAsset,userId:string,dependencies:DownloadDependencies={fetchBlob,downloadBlob,deleteServer:id=>apiRequest(`/api/media/${id}`,{method:'DELETE',body:JSON.stringify({reason:'downloaded'})})}):Promise<{serverDeleted:boolean;message:string}>{
  const previous=media.stored_on_device?await getLocalAsset(userId,media.id):null;
  if(media.stored_on_device&&!previous)throw new Error('Файл больше не доступен в локальной библиотеке');
  const blob=previous?.blob??await dependencies.fetchBlob(`/api/media/${media.id}/content`);
  if(!blob.size)throw new Error('Получен пустой файл. Серверная копия не удалена.');
  if(Number(media.byte_size)>0&&blob.size!==Number(media.byte_size))throw new Error('Размер скачанного файла не совпал. Серверная копия не удалена. Повторите скачивание.');
  if(blob.type&&media.mime_type&&blob.type.split('/')[0]!==media.mime_type.split('/')[0])throw new Error('Получен файл другого типа. Серверная копия не удалена.');
  if(media.library_scope==='shared'){
    dependencies.downloadBlob(blob,media.original_name||`vgen-${media.id}`);
    return{serverDeleted:false,message:'Скачивание начато. Общая библиотека не изменена.'};
  }
  const row:StoredAsset={key:keyFor(userId,media.id),userId,media:{...media,stored_on_device:true},blob,serverDeletionPending:previous?.serverDeletionPending??true};
  await transaction('readwrite',store=>store.put(row));
  const readback=await getLocalAsset(userId,media.id);
  if(!readback||readback.blob.size!==blob.size)throw new Error('Не удалось проверить локальную копию. Серверная копия не удалена.');
  await indexAsset(row);
  dependencies.downloadBlob(blob,media.original_name||`vgen-${media.id}`);
  void navigator.storage?.persist?.().catch(()=>false);
  if(!row.serverDeletionPending)return{serverDeleted:true,message:'Скачивание начато. Копия доступна в приложении на этом устройстве.'};
  try{
    await dependencies.deleteServer(media.id);await markLocalAssetServerDeleted(userId,media.id);
    return{serverDeleted:true,message:'Копия сохранена в приложении на этом устройстве. Скачивание начато; серверная копия удалена.'};
  }catch(error){return{serverDeleted:false,message:`Локальная копия сохранена. Серверная копия пока остаётся: ${(error as Error).message}`}}
}
